import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faPlus,
  faSave,
  faSearch,
  faTrash,
  faUserShield,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Alert from "../../../components/common/Alert";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import { useAuth } from "../../../contexts/AuthContext";
import RolePermissionService from "../../../services/core/RolePermissionService";

const emptyRole = {
  id: null,
  key: "",
  name: "",
  description: "",
  enabled: true,
  systemRole: false,
  sourceType: "IDAX",
  sourceKey: "",
};

const RolePermissionsPage = () => {
  const { t } = useTranslation();
  const { user, hasPermission, refreshMe } = useAuth();
  const tenantId =
    localStorage.getItem("selectedTenant") ||
    user?.currentTenantId ||
    user?.tenantId;
  const service = useMemo(
    () => tenantId ? new RolePermissionService(tenantId) : null,
    [tenantId],
  );
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(emptyRole);
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [activeTab, setActiveTab] = useState("permissions");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const canManage = hasPermission("system.roles.manage");

  const loadBaseData = async () => {
    if (!service) return;
    setLoading(true);
    try {
      const [roleRows, permissionRows, userRows] = await Promise.all([
        service.listRoles(),
        service.catalog(),
        service.users(),
      ]);
      setRoles(roleRows || []);
      setCatalog(permissionRows || []);
      setUsers(userRows || []);
      if (!selectedRole.id && roleRows?.length) {
        await selectRole(roleRows[0], roleRows);
      }
      setError("");
    } catch (loadError) {
      setError(loadError?.message || t("rolesPage.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const selectRole = async (role, currentRoles = roles) => {
    if (!role?.id || !service) return;
    const fullRole = currentRoles.find((candidate) => candidate.id === role.id) || role;
    setSelectedRole({ ...fullRole });
    setActiveTab("permissions");
    try {
      const permissions = await service.rolePermissions(role.id);
      setSelectedPermissions(new Set(permissions || []));
    } catch (loadError) {
      setError(loadError?.message || t("rolesPage.errors.permissions"));
    }
  };

  const groupedCatalog = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const groups = new Map();
    catalog.forEach((permission) => {
      const haystack = `${permission.code} ${permission.resourceKey} ${permission.description || ""}`.toLowerCase();
      if (normalizedSearch && !haystack.includes(normalizedSearch)) return;
      const module = permission.moduleKey || "other";
      const resource = permission.resourceKey || permission.code;
      if (!groups.has(module)) groups.set(module, new Map());
      if (!groups.get(module).has(resource)) groups.get(module).set(resource, []);
      groups.get(module).get(resource).push(permission);
    });
    return Array.from(groups.entries()).map(([module, resources]) => ({
      module,
      resources: Array.from(resources.entries()).map(([resource, permissions]) => ({
        resource,
        permissions,
      })),
    }));
  }, [catalog, search]);

  const togglePermission = (code) => {
    if (!canManage) return;
    setSelectedPermissions((current) => {
      const next = new Set(current);
      const permission = catalog.find((item) => item.code === code);
      if (next.has(code)) {
        next.delete(code);
        if (permission?.fieldKey && permission.actionKey === "view") {
          next.delete(`${permission.resourceKey}.field.${permission.fieldKey}.edit`);
        }
      } else {
        next.add(code);
        if (permission?.fieldKey && permission.actionKey === "edit") {
          next.add(`${permission.resourceKey}.field.${permission.fieldKey}.view`);
        }
      }
      return next;
    });
  };

  const toggleResource = (permissions, enabled) => {
    if (!canManage) return;
    setSelectedPermissions((current) => {
      const next = new Set(current);
      permissions.forEach(({ code }) => enabled ? next.add(code) : next.delete(code));
      return next;
    });
  };

  const startNewRole = () => {
    setSelectedRole({ ...emptyRole });
    setSelectedPermissions(new Set());
    setActiveTab("permissions");
  };

  const saveRole = async () => {
    if (!service || !canManage) return;
    setSaving(true);
    try {
      const payload = {
        key: selectedRole.key,
        name: selectedRole.name,
        description: selectedRole.description,
        enabled: selectedRole.enabled,
        sourceType: selectedRole.sourceType || "IDAX",
        sourceKey: selectedRole.sourceKey || null,
        permissions: Array.from(selectedPermissions),
      };
      const saved = selectedRole.id
        ? await service.updateRole(selectedRole.id, payload)
        : await service.createRole(payload);
      const roleRows = await service.listRoles();
      setRoles(roleRows || []);
      await selectRole(saved, roleRows);
      await refreshMe();
      setSuccess(t("rolesPage.saved"));
    } catch (saveError) {
      setError(saveError?.message || t("rolesPage.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async () => {
    if (!service || !selectedRole.id || selectedRole.systemRole) return;
    setSaving(true);
    try {
      await service.deleteRole(selectedRole.id);
      const roleRows = await service.listRoles();
      setRoles(roleRows || []);
      setShowDelete(false);
      if (roleRows.length) await selectRole(roleRows[0], roleRows);
      else startNewRole();
      setSuccess(t("rolesPage.deleted"));
    } catch (deleteError) {
      setError(deleteError?.message || t("rolesPage.errors.delete"));
    } finally {
      setSaving(false);
    }
  };

  const toggleUserRole = async (targetUser, checked) => {
    if (!service || !selectedRole.id || !canManage) return;
    const nextRoleIds = new Set(targetUser.roleIds || []);
    if (checked) nextRoleIds.add(selectedRole.id);
    else nextRoleIds.delete(selectedRole.id);
    try {
      await service.updateUserRoles(targetUser.userId, Array.from(nextRoleIds));
      setUsers((current) => current.map((candidate) =>
        candidate.userId === targetUser.userId
          ? { ...candidate, roleIds: Array.from(nextRoleIds) }
          : candidate
      ));
      if (targetUser.userId === user?.userId) await refreshMe();
    } catch (assignError) {
      setError(assignError?.message || t("rolesPage.errors.assign"));
    }
  };

  if (!tenantId) {
    return <Alert type="error" message={t("rolesPage.errors.noTenant")} />;
  }

  return (
    <div className="roles-page">
      <PageHeader
        title={t("rolesPage.title")}
        subtitle={t("rolesPage.subtitle")}
        icon={faUserShield}
      >
        {canManage && (
          <>
            <button type="button" className="btn btn-default" onClick={startNewRole}>
              <FontAwesomeIcon icon={faPlus} /> <span>{t("rolesPage.newRole")}</span>
            </button>
            <button type="button" className="btn btn-default" onClick={() => service.exportFlyway()}>
              <FontAwesomeIcon icon={faDownload} /> <span>{t("rolesPage.exportFlyway")}</span>
            </button>
          </>
        )}
      </PageHeader>

      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess("")} />}

      {loading ? (
        <Spinner size="large" message={t("common.loading")} />
      ) : (
        <div className="roles-workspace">
          <aside className="roles-list-panel">
            <div className="roles-list-header">
              <strong>{t("rolesPage.roles")}</strong>
              <span>{roles.length}</span>
            </div>
            <div className="roles-list">
              {roles.map((role) => (
                <button
                  type="button"
                  key={role.id}
                  className={selectedRole.id === role.id ? "is-active" : ""}
                  onClick={() => selectRole(role)}
                >
                  <span className="roles-list-name">{role.name}</span>
                  <span className="roles-list-meta">
                    {role.permissionCount} {t("rolesPage.permissionsShort")} | {role.userCount} {t("rolesPage.usersShort")}
                  </span>
                  <span className={`roles-source-badge source-${String(role.sourceType).toLowerCase()}`}>
                    {role.sourceType}
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <section className="role-editor">
            <div className="role-editor-toolbar">
              <div className="role-identity-fields">
                <label>
                  <span>{t("rolesPage.fields.name")}</span>
                  <input
                    className="form-control"
                    value={selectedRole.name || ""}
                    disabled={!canManage}
                    onChange={(event) => setSelectedRole((current) => ({ ...current, name: event.target.value }))}
                  />
                </label>
                <label>
                  <span>{t("rolesPage.fields.key")}</span>
                  <input
                    className="form-control"
                    value={selectedRole.key || ""}
                    disabled={!canManage || selectedRole.systemRole}
                    onChange={(event) => setSelectedRole((current) => ({ ...current, key: event.target.value }))}
                  />
                </label>
                <label className="role-description-field">
                  <span>{t("rolesPage.fields.description")}</span>
                  <input
                    className="form-control"
                    value={selectedRole.description || ""}
                    disabled={!canManage}
                    onChange={(event) => setSelectedRole((current) => ({ ...current, description: event.target.value }))}
                  />
                </label>
                <label>
                  <span>{t("rolesPage.fields.source")}</span>
                  <select
                    className="form-control"
                    value={selectedRole.sourceType || "IDAX"}
                    disabled={!canManage || selectedRole.systemRole}
                    onChange={(event) => setSelectedRole((current) => ({ ...current, sourceType: event.target.value }))}
                  >
                    <option value="IDAX">IDAX</option>
                    <option value="AX4_USER_GROUP">AX4 User Group</option>
                    <option value="EXTERNAL">External</option>
                  </select>
                </label>
                <label>
                  <span>{t("rolesPage.fields.sourceKey")}</span>
                  <input
                    className="form-control"
                    value={selectedRole.sourceKey || ""}
                    disabled={!canManage || selectedRole.systemRole}
                    placeholder={t("rolesPage.fields.sourceKeyPlaceholder")}
                    onChange={(event) => setSelectedRole((current) => ({ ...current, sourceKey: event.target.value }))}
                  />
                </label>
                <label className="role-enabled-field">
                  <input
                    type="checkbox"
                    checked={selectedRole.enabled !== false}
                    disabled={!canManage}
                    onChange={(event) => setSelectedRole((current) => ({ ...current, enabled: event.target.checked }))}
                  />
                  <span>{t("rolesPage.fields.enabled")}</span>
                </label>
              </div>
              {canManage && (
                <div className="role-editor-actions">
                  {selectedRole.id && !selectedRole.systemRole && (
                    <button type="button" className="btn btn-default" onClick={() => setShowDelete(true)}>
                      <FontAwesomeIcon icon={faTrash} /> <span>{t("common.delete")}</span>
                    </button>
                  )}
                  <button type="button" className="btn btn-primary" onClick={saveRole} disabled={saving || !selectedRole.name}>
                    <FontAwesomeIcon icon={faSave} /> <span>{t("common.save")}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="role-editor-tabs" role="tablist">
              <button
                type="button"
                className={activeTab === "permissions" ? "is-active" : ""}
                onClick={() => setActiveTab("permissions")}
              >
                <FontAwesomeIcon icon={faUserShield} /> {t("rolesPage.tabs.permissions")}
              </button>
              <button
                type="button"
                className={activeTab === "users" ? "is-active" : ""}
                onClick={() => setActiveTab("users")}
                disabled={!selectedRole.id}
              >
                <FontAwesomeIcon icon={faUsers} /> {t("rolesPage.tabs.users")}
              </button>
            </div>

            {activeTab === "permissions" ? (
              <div className="permissions-editor">
                <div className="permissions-search">
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("rolesPage.searchPermissions")}
                  />
                  <span>{selectedPermissions.size} / {catalog.length}</span>
                </div>
                {groupedCatalog.map((group) => (
                  <section className="permission-module" key={group.module}>
                    <h3>{t(`permissionModules.${group.module}`, { defaultValue: group.module })}</h3>
                    <div className="permission-resource-list">
                      {group.resources.map(({ resource, permissions }) => {
                        const allSelected = permissions.every(({ code }) => selectedPermissions.has(code));
                        const resourcePermissions = permissions.filter((permission) => !permission.fieldKey);
                        const fieldPermissions = Array.from(
                          permissions
                            .filter((permission) => permission.fieldKey)
                            .reduce((fields, permission) => {
                              if (!fields.has(permission.fieldKey)) fields.set(permission.fieldKey, []);
                              fields.get(permission.fieldKey).push(permission);
                              return fields;
                            }, new Map())
                            .entries(),
                        );
                        const resourceLabel = resourcePermissions[0]?.description || permissions[0]?.resourceKey;
                        return (
                          <div className="permission-resource-row" key={resource}>
                            <label className="permission-resource-title">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                disabled={!canManage}
                                onChange={(event) => toggleResource(permissions, event.target.checked)}
                              />
                              <span>
                                {resourceLabel && resourceLabel.includes(".")
                                  ? t(resourceLabel, { defaultValue: resource })
                                  : resourceLabel || resource}
                              </span>
                            </label>
                            <div className="permission-actions">
                              {resourcePermissions.map((permission) => (
                                <label key={permission.code}>
                                  <input
                                    type="checkbox"
                                    checked={selectedPermissions.has(permission.code)}
                                    disabled={!canManage}
                                    onChange={() => togglePermission(permission.code)}
                                  />
                                  <span>{t(permission.labelKey, { defaultValue: permission.actionKey })}</span>
                                </label>
                              ))}
                            </div>
                            {fieldPermissions.length > 0 && (
                              <details className="field-permissions">
                                <summary>
                                  {t("rolesPage.fieldPermissions")} ({fieldPermissions.length})
                                </summary>
                                <div className="field-permissions-grid">
                                  {fieldPermissions.map(([fieldKey, items]) => (
                                    <div className="field-permission-row" key={fieldKey}>
                                      <span>
                                        {items[0]?.description?.includes(".")
                                          ? t(items[0].description, { defaultValue: fieldKey })
                                          : items[0]?.description || fieldKey}
                                      </span>
                                      <div className="permission-actions">
                                        {items.map((permission) => (
                                          <label key={permission.code}>
                                            <input
                                              type="checkbox"
                                              checked={selectedPermissions.has(permission.code)}
                                              disabled={!canManage}
                                              onChange={() => togglePermission(permission.code)}
                                            />
                                            <span>{t(permission.labelKey, { defaultValue: permission.actionKey })}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="role-users-list">
                <div className="role-users-header">
                  <span></span>
                  <span>{t("rolesPage.userColumns.name")}</span>
                  <span>{t("rolesPage.userColumns.subject")}</span>
                  <span>{t("rolesPage.userColumns.email")}</span>
                  <span>{t("rolesPage.userColumns.role")}</span>
                </div>
                {users.map((targetUser) => (
                  <label key={targetUser.userId}>
                    <input
                      type="checkbox"
                      checked={(targetUser.roleIds || []).includes(selectedRole.id)}
                      disabled={!canManage}
                      onChange={(event) => toggleUserRole(targetUser, event.target.checked)}
                    />
                    <span className="role-user-name">{targetUser.displayName || targetUser.email}</span>
                    <span className="role-user-subject">{targetUser.subject || "-"}</span>
                    <span>{targetUser.email}</span>
                    <span className="roles-source-badge">{targetUser.legacyRole}</span>
                  </label>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={deleteRole}
        title={t("rolesPage.deleteTitle")}
        message={t("rolesPage.deleteConfirm", { name: selectedRole.name })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        type="danger"
        isProcessing={saving}
      />
    </div>
  );
};

export default RolePermissionsPage;
